import { useEffect } from 'react'

import { captureException } from '@sentry/nextjs'

import AnimatedLoadingPage from '@components/AnimatedLoadingPage'
import ErrorBoundary from '@components/ErrorBoundary'
import MetadataHead from '@components/MetadataHead'

import useAppQueryState from '@hooks/useAppQueryState'
import useRouterParams from '@hooks/useRouterParams'

import { useScoredCategoriesQuery } from '@queries/useScoredCategoriesQuery'

import { getCardByCategory } from '@utils/getCardByCategory'

import ScoredCard from './components/ScoredCard'
import ScoredCardError from './components/ScoredCardError'
import ScoredCardNotFound from './components/ScoredCardNotFound'

const ScoredCardRoute = () => {
    const routerParams = useRouterParams()
    const { goToRouteWithAppState, appQueryState } = useAppQueryState()

    const scoredCardResponse = useScoredCategoriesQuery({
        body: {
            scoreId: appQueryState.scoreId as string,
            customerId: appQueryState.customerId as string,
            category: appQueryState.categoryId!,
        },
        select: selectData => {
            return {
                card: getCardByCategory(selectData.scoredCategories!, {
                    cardId: routerParams.cardId,
                    category: appQueryState.categoryId!,
                    subProductCode: appQueryState.subProductCode,
                }),
                scoredCategories: selectData.scoredCategories,
                thirdPartyReference: selectData.thirdPartyReference,
            }
        },
    })

    const redirectToTable =
        scoredCardResponse.isSuccess &&
        scoredCardResponse.data?.card == null &&
        appQueryState.isCardSpecific

    useEffect(() => {
        if (redirectToTable) {
            goToRouteWithAppState(appQueryState.category)
        }
    }, [redirectToTable])

    switch (true) {
        case scoredCardResponse.isLoading:
        case redirectToTable:
            return <AnimatedLoadingPage />
        case scoredCardResponse.isSuccess &&
            scoredCardResponse.data?.card != null:
            return (
                <>
                    <MetadataHead metadata={undefined} />
                    <ErrorBoundary
                        fallback={<ScoredCardError />}
                        onError={({ error }) => captureException(error)}
                    >
                        <ScoredCard
                            scoredCard={scoredCardResponse.data!.card!}
                            thirdPartyReference={
                                scoredCardResponse.data!.thirdPartyReference
                            }
                            scoredCategories={
                                scoredCardResponse.data!.scoredCategories
                            }
                        />
                    </ErrorBoundary>
                </>
            )
        case scoredCardResponse.isSuccess &&
            scoredCardResponse.data?.card == null:
            return (
                <>
                    <MetadataHead metadata={undefined} />
                    <ScoredCardNotFound />
                </>
            )
        case scoredCardResponse.isError:
        default:
            return <ScoredCardError />
    }
}

export default ScoredCardRoute
